import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [requiresMfaSetup, setRequiresMfaSetup] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'email' | 'totp'>('email');
  const [sendingCode, setSendingCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [mfaSetupMethod, setMfaSetupMethod] = useState<'email' | 'totp' | null>(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [totpManualKey, setTotpManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [settingUpMfa, setSettingUpMfa] = useState(false);
  const [ssoInfo, setSsoInfo] = useState<{ sso_enabled: boolean; company_id?: number; sso_type?: string } | null>(null);
  const [checkingSso, setCheckingSso] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const { login } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();

  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;
    
    setCheckingSso(true);
    try {
      const api = (await import('../api')).default;
      const response = await api.post('/auth/sso/check', { email });
      setSsoInfo(response.data);
    } catch (err) {
      console.error('Error checking SSO:', err);
    } finally {
      setCheckingSso(false);
    }
  };

  const handleSsoLogin = () => {
    if (ssoInfo?.company_id) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      window.location.href = `${apiUrl}/auth/sso/init?company_id=${ssoInfo.company_id}`;
    }
  };

  const handleSendMfaCode = async () => {
    setSendingCode(true);
    setError('');
    try {
      const api = (await import('../api')).default;
      await api.post('/auth/mfa/send-login-code', { email, password });
      setRequiresMfa(true);
      setError('');
    } catch (err: any) {
      console.error('Error sending MFA code:', err);
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password, requiresMfa ? mfaCode : undefined, rememberDevice);
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.requiresMfaSetup) {
        // Company requires MFA but user hasn't set it up
        setRequiresMfaSetup(true);
        setError('');
      } else if (err.requiresMfa) {
        // MFA is required
        setRequiresMfa(true);
        setMfaMethod(err.mfaMethod || 'email');
        // Only send email code if method is email and not already sent
        if (err.mfaMethod !== 'totp' && !requiresMfa) {
          await handleSendMfaCode();
        }
      } else if (err.response) {
        setError(err.response.data?.error || 'Login failed');
      } else if (err.request) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const port = apiUrl.match(/:(\d+)/)?.[1] || '5000';
        setError(`Cannot connect to server. Please ensure the backend is running on port ${port}.`);
      } else {
        setError(err.message || 'Login failed');
      }
    }
  };

  const handleStartMfaSetup = (method: 'email' | 'totp') => {
    setMfaSetupMethod(method);
    setError('');
    if (method === 'email') {
      handleSendMfaSetupCode();
    } else {
      handleGenerateTotpSetup();
    }
  };

  const handleSendMfaSetupCode = async () => {
    setSendingCode(true);
    setError('');
    try {
      const api = (await import('../api')).default;
      await api.post('/auth/mfa/setup/send-code', { email, password });
      setError('');
    } catch (err: any) {
      console.error('Error sending MFA setup code:', err);
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleGenerateTotpSetup = async () => {
    setSettingUpMfa(true);
    setError('');
    try {
      const api = (await import('../api')).default;
      const response = await api.post('/auth/mfa/setup/totp/generate', { email, password });
      setTotpSecret(response.data.secret);
      setTotpQrCode(response.data.qrCode);
      setTotpManualKey(response.data.manualEntryKey);
    } catch (err: any) {
      console.error('Error generating TOTP setup:', err);
      setError(err.response?.data?.error || 'Failed to generate authenticator setup');
    } finally {
      setSettingUpMfa(false);
    }
  };

  const handleCompleteMfaSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingUpMfa(true);
    setError('');

    try {
      const api = (await import('../api')).default;
      
      if (mfaSetupMethod === 'email') {
        await api.post('/auth/mfa/setup/enable-email', { email, password, code: verificationCode });
      } else {
        await api.post('/auth/mfa/setup/totp/verify', { email, password, secret: totpSecret, code: verificationCode });
      }

      // MFA is now set up, try logging in again
      setRequiresMfaSetup(false);
      setMfaSetupMethod(null);
      setVerificationCode('');
      setTotpSecret('');
      setTotpQrCode('');
      setTotpManualKey('');
      
      // Automatically log in now that MFA is set up
      await login(email, password, undefined, rememberDevice);
      navigate('/');
    } catch (err: any) {
      console.error('Error completing MFA setup:', err);
      setError(err.response?.data?.error || 'Failed to complete MFA setup');
    } finally {
      setSettingUpMfa(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotPasswordLoading(true);
    setForgotPasswordSuccess(false);

    try {
      const api = (await import('../api')).default;
      await api.post('/auth/password-reset/request', { email: forgotPasswordEmail });
      setForgotPasswordSuccess(true);
    } catch (err: any) {
      console.error('Error requesting password reset:', err);
      setError(err.response?.data?.error || 'Failed to send password reset email');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 relative">
      <button
        onClick={toggleDarkMode}
        className="absolute top-4 right-4 p-2 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        aria-label="Toggle dark mode"
      >
        {darkMode ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>
      <div className="max-w-md w-full space-y-8 p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            NxtWorks Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Sign in to your account
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
              />
            </div>
            {!ssoInfo?.sso_enabled && (
              <div>
                <label htmlFor="password" title="Password is not required when signing in with SSO" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required={!ssoInfo?.sso_enabled}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          {requiresMfaSetup && !mfaSetupMethod && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Multi-Factor Authentication Required
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
                Your company requires Multi-Factor Authentication. Please set up MFA to continue.
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleStartMfaSetup('email')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Set Up Email MFA
                </button>
                <button
                  type="button"
                  onClick={() => handleStartMfaSetup('totp')}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Set Up Authenticator App
                </button>
              </div>
            </div>
          )}

          {requiresMfaSetup && mfaSetupMethod && (
            <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md p-4 mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
                {mfaSetupMethod === 'email' ? 'Set Up Email MFA' : 'Set Up Authenticator App'}
              </h3>
              {mfaSetupMethod === 'totp' && totpQrCode && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Scan this QR code with your authenticator app:
                  </p>
                  <div className="flex justify-center mb-3">
                    <img src={totpQrCode} alt="QR Code" className="border border-gray-300 dark:border-gray-600 rounded p-2 bg-white" />
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Or enter this key manually:</p>
                    <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-300 dark:border-gray-600">
                      <code className="text-xs font-mono text-gray-900 dark:text-gray-100 break-all">{totpManualKey}</code>
                    </div>
                  </div>
                </>
              )}
              <form onSubmit={handleCompleteMfaSetup}>
                <div className="mb-3">
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {mfaSetupMethod === 'email' ? 'Enter the 6-digit code sent to your email' : 'Enter the 6-digit code from your authenticator app'}
                  </label>
                  <input
                    id="verificationCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-widest"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={settingUpMfa}
                    className="flex-1 flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {settingUpMfa ? 'Setting Up...' : 'Complete Setup & Sign In'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMfaSetupMethod(null);
                      setVerificationCode('');
                      setTotpSecret('');
                      setTotpQrCode('');
                      setTotpManualKey('');
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {ssoInfo?.sso_enabled && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-md p-4 mb-4">
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-3 text-center">
                SSO is enabled for your account.
              </p>
              <button
                type="button"
                onClick={handleSsoLogin}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Sign in with {ssoInfo.sso_type === 'azure' ? 'Microsoft Azure' : ssoInfo.sso_type === 'google' ? 'Google' : 'SSO'}
              </button>
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setSsoInfo(null)}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Use password instead
                </button>
              </div>
            </div>
          )}

              {!ssoInfo?.sso_enabled && !requiresMfaSetup && (
                <>
                  {requiresMfa && (
                    <>
                      <div>
                        <label htmlFor="mfaCode" className="sr-only">
                          Verification Code
                        </label>
                        <input
                          id="mfaCode"
                          name="mfaCode"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          required={requiresMfa}
                          className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
                          placeholder="000000"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                          {mfaMethod === 'totp' 
                            ? 'Enter the 6-digit code from your authenticator app'
                            : 'Enter the 6-digit code sent to your email'}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="rememberDevice"
                          name="rememberDevice"
                          type="checkbox"
                          checked={rememberDevice}
                          onChange={(e) => setRememberDevice(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <label htmlFor="rememberDevice" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                          Remember this device for 30 days
                        </label>
                      </div>
                    </>
                  )}

                  <div>
                    <button
                      type="submit"
                      className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {requiresMfa ? 'Verify & Sign in' : 'Sign in'}
                    </button>
                  </div>

                  {requiresMfa && mfaMethod === 'email' && (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleSendMfaCode}
                        disabled={sendingCode}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 disabled:opacity-50"
                      >
                        {sendingCode ? 'Sending...' : 'Resend Code'}
                      </button>
                    </div>
                  )}

                  {!requiresMfa && (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </>
              )}
        </form>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
            {forgotPasswordSuccess ? (
              <div>
                <p className="text-sm text-green-600 dark:text-green-400 mb-4">
                  If an account exists with this email, a password reset link has been sent. Please check your email.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordSuccess(false);
                  }}
                  className="w-full text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                >
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Reset Password
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <div className="mb-3">
                  <input
                    type="email"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Email address"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setError('');
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
